import { type AudioPeaks } from '@tunasong/models'
import { storageApi } from '@tunasong/redux'
import type { Audio } from '@tunasong/schemas'
import invariant from 'tiny-invariant'
import { useRenderPeaks } from './render-peaks.js'

interface Peaks {
  media?: Audio
  /** URL of the peaks JSON, for media served as a projection (`PlayableMedia.peaksUrl`); the stored path wins */
  peaksUrl?: string
  /** Get the peaks from this url */
  url?: string
  /** Render Peaks if the peaks are not available */
  renderPeaks?: boolean
}

export const usePeaks = ({ media, peaksUrl, url: sourceUrl, renderPeaks = false }: Peaks) => {
  let peaksPath = media?.features?.peaks?.data

  invariant(!(sourceUrl && media), 'Cannot provide both media and url')

  /** Legacy peaks path */
  if (typeof peaksPath === 'object') {
    peaksPath = undefined
  }

  const mediaUrl = media?.storageUrls?.url

  const { currentData: pathPeaks, isLoading: pathLoading } = storageApi.useGetFileJSONQuery(
    { storagePath: peaksPath ?? '' },
    { skip: Boolean(!peaksPath) }
  )

  /** A projection carries no storage path, only the URL it was served with */
  const { currentData: urlPeaks, isLoading: urlLoading } = storageApi.useGetFileJSONByUrlQuery(
    { url: peaksUrl ?? '' },
    { skip: Boolean(peaksPath) || !peaksUrl }
  )

  const loadedPeaks = pathPeaks ?? urlPeaks
  const storageLoading = pathLoading || urlLoading

  const url = sourceUrl ?? mediaUrl

  /** If we don't have a peaksPath and wants to renderPeaks, use fallback method */
  const fallbackUrl = url && renderPeaks && !storageLoading && !loadedPeaks ? url : null

  const { peaks: renderedPeaks, loading: renderedLoading } = useRenderPeaks(fallbackUrl)

  const peaks = loadedPeaks ?? renderedPeaks
  const loading = storageLoading || renderedLoading

  return { peaks: peaks as AudioPeaks | undefined, loading }
}
